/**
 * Urban Entities API Client
 *
 * @remarks
 * Cliente HTTP para los endpoints de /api/v1/urban-entities.
 * Mismo patrón de autenticación que waste-api y mobility-api:
 * axios + interceptor que lee el token del store de auth.
 *
 * @module urban-inventory/services
 */

import axios, { type AxiosInstance } from 'axios';
import { useAuthStore } from '../../../auth/useAuthStore';
import type { UrbanEntity, UrbanEntityFilters } from '../types/urban-entity.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// ─── Auth token helper ────────────────────────────────────────────────────────

function getAuthToken(): string | null {
  const storeToken = useAuthStore.getState().token;
  if (storeToken) {
    return storeToken;
  }

  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { token?: string }; token?: string };
    return parsed.state?.token ?? parsed.token ?? null;
  } catch {
    return null;
  }
}

// ─── Axios instance ───────────────────────────────────────────────────────────

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/urban-entities`,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[UrbanEntitiesAPI] Unauthorized — token may be expired');
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

const api = createApiInstance();

// ─── API client ───────────────────────────────────────────────────────────────

export const urbanEntitiesApi = {
  /**
   * Lista todas las urban_entities de un proyecto, con filtros opcionales.
   *
   * GET /api/v1/urban-entities?projectId=&entityType=&status=
   */
  async list(projectId: string, filters?: UrbanEntityFilters): Promise<UrbanEntity[]> {
    const params: Record<string, string> = { projectId };
    if (filters?.entityType) params.entityType = filters.entityType;
    if (filters?.status) params.status = filters.status;

    const response = await api.get<UrbanEntity[]>('/', { params });
    return response.data;
  },

  /**
   * Obtiene una urban_entity por ID.
   *
   * GET /api/v1/urban-entities/:id?projectId=
   */
  async get(projectId: string, id: string): Promise<UrbanEntity> {
    const response = await api.get<UrbanEntity>(`/${id}`, { params: { projectId } });
    return response.data;
  },
};

// ─── Error helper ─────────────────────────────────────────────────────────────

export function getUrbanEntitiesApiError(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    const data = (error.response as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  if (error instanceof Error) return error.message;
  return 'Error desconocido en UrbanEntitiesAPI';
}
